<script setup lang="ts">
import type { Schemas } from "#shopware"

interface ProductBadgesProps {
    product: Schemas["Product"]
    showTopSeller?: boolean
    showNew?: boolean
    showSale?: boolean
    showCheapestPrice?: boolean
    variant?: 'default' | 'floating'
}

const props = withDefaults(defineProps<ProductBadgesProps>(), {
    showTopSeller: true,
    showNew: true,
    showSale: true,
    showCheapestPrice: false,
    variant: 'default'
})

const productRef = computed(() => props.product)
const {
    price,
    displayFrom,
    isListPrice,
    displayCheapestPrice,
    cheapestPrice
} = useProductPriceCustom(productRef)

const cheapestListPrice = computed(() => props.product?.calculatedCheapestPrice?.listPrice?.price)
const cheapestListPricePercentage = computed(() => props.product?.calculatedCheapestPrice?.listPrice?.percentage)

const badgeClasses = computed(() => ({
    'inline-flex items-center px-2.5 text-sm font-bold text-base': true,
    '': props.variant === 'floating',
    'rounded-md mr-2': props.variant === 'default',
    'z-10': true
}))
const isNewProduct = computed(() => props.product?.properties?.some(p => p.group?.name === 'IsCurrentSeason' && p.name === '1'))
</script>

<template>
    <div class="product-badges absolute -left-1 flex flex-col items-start">
        <span
            v-if="showNew && (isNewProduct || (product?.customFields as Record<string, unknown>)?.gisy_is_current_season === true)"
            :class="badgeClasses"
            data-testid="product-new-badge"
        >
            {{ $t("product.badges.new") }}
        </span>

        <span
            v-if="showSale && ((isListPrice && !displayFrom && price?.listPrice?.percentage) || ((displayCheapestPrice || showCheapestPrice) && cheapestListPrice && cheapestListPrice !== cheapestPrice))"
            :class="badgeClasses"
            data-testid="product-sale-badge"
        >
            <template v-if="isListPrice && !displayFrom && price?.listPrice?.percentage">
                -{{ Math.round(price.listPrice.percentage) }}%
            </template>
            <template v-else-if="cheapestListPricePercentage">
                -{{ Math.round(cheapestListPricePercentage) }}%
            </template>
        </span>

        <span
            v-if="showTopSeller && product.markAsTopseller"
            :class="badgeClasses"
            data-testid="product-topseller-badge"
        >
            {{ $t("product.badges.topseller") }}
        </span>
    </div>
</template>